import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowRight, Check, X, GitCompareArrows } from 'lucide-react';
import Layout from '@/components/Layout';
import { BracketPicks, BracketPick } from '@/types/bracket';
import { fetchBracketEntry } from '@/lib/bracket-queries';
import { fetchSeries } from '@/lib/supabase-queries';
import { supabase } from '@/integrations/supabase/client';
import { UserAvatar } from '@/components/UserAvatar';

// ── Types ────────────────────────────────────────────────────────────────────

interface UserBracketData {
  displayName: string;
  picks: BracketPicks;
  avatarId: number | null;
}

interface SlotComparison {
  slotId: string;
  label: string;
  round: string;
  pick1: BracketPick | null;
  pick2: BracketPick | null;
  match: 'same' | 'different' | 'missing';
}

// ── Round labels (Hebrew) ────────────────────────────────────────────────────

const ROUND_LABELS: Record<string, string> = {
  round1: 'סיבוב ראשון',
  first_round: 'סיבוב ראשון',
  west_semis: 'חצי גמר מערב',
  east_semis: 'חצי גמר מזרח',
  west_finals: 'גמר מערב',
  east_finals: 'גמר מזרח',
  finals: 'הגמר',
};

// ── Slot ordering for display ────────────────────────────────────────────────

const ROUND_ORDER = [
  'round1',
  'first_round',
  'west_semis',
  'east_semis',
  'west_finals',
  'east_finals',
  'finals',
];

// ── West teams heuristic (same as use-bracket.ts) ────────────────────────────

const WEST_TEAMS = new Set([
  'Oklahoma City Thunder', 'Houston Rockets', 'Los Angeles Lakers',
  'Golden State Warriors', 'Denver Nuggets', 'Memphis Grizzlies',
  'LA Clippers', 'Dallas Mavericks', 'Minnesota Timberwolves',
  'Sacramento Kings', 'Phoenix Suns', 'Utah Jazz',
  'Portland Trail Blazers', 'New Orleans Pelicans',
  'Thunder', 'Rockets', 'Lakers', 'Warriors', 'Nuggets', 'Grizzlies',
  'Clippers', 'Mavericks', 'Timberwolves', 'Kings', 'Suns', 'Jazz',
  'Trail Blazers', 'Pelicans',
]);

// ── Virtual slot IDs for R2+ ─────────────────────────────────────────────────

const VIRTUAL_SLOTS = [
  { id: 'r2_west_0', round: 'west_semis', label: 'חצי גמר מערב 1' },
  { id: 'r2_west_1', round: 'west_semis', label: 'חצי גמר מערב 2' },
  { id: 'r2_east_0', round: 'east_semis', label: 'חצי גמר מזרח 1' },
  { id: 'r2_east_1', round: 'east_semis', label: 'חצי גמר מזרח 2' },
  { id: 'cf_west', round: 'west_finals', label: 'גמר מערב' },
  { id: 'cf_east', round: 'east_finals', label: 'גמר מזרח' },
  { id: 'finals', round: 'finals', label: 'הגמר הגדול' },
];

// ── Component ─────────────────────────────────────────────────────────────────

const BracketComparePage = () => {
  const { userId1, userId2 } = useParams<{ userId1: string; userId2: string }>();
  const [user1Data, setUser1Data] = useState<UserBracketData | null>(null);
  const [user2Data, setUser2Data] = useState<UserBracketData | null>(null);
  const [seriesMap, setSeriesMap] = useState<Record<string, { team_home: string; team_away: string; round: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId1 || !userId2) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [entry1, entry2, allSeries, avatarData] = await Promise.all([
          fetchBracketEntry(userId1),
          fetchBracketEntry(userId2),
          fetchSeries(),
          supabase
            .from('group_members')
            .select('user_id, avatar_id')
            .in('user_id', [userId1, userId2]),
        ]);

        if (!entry1 && !entry2) {
          setError('לא נמצאו ברקטים לשני המשתתפים');
          return;
        }

        const avatarMap: Record<string, number | null> = {};
        (avatarData.data || []).forEach((row: { user_id: string; avatar_id: number | null }) => {
          if (avatarMap[row.user_id] === undefined) {
            avatarMap[row.user_id] = row.avatar_id ?? null;
          }
        });

        setUser1Data({
          displayName: entry1?.display_name || 'לא נמצא',
          picks: (entry1?.picks as BracketPicks) || {},
          avatarId: avatarMap[userId1] ?? null,
        });

        setUser2Data({
          displayName: entry2?.display_name || 'לא נמצא',
          picks: (entry2?.picks as BracketPicks) || {},
          avatarId: avatarMap[userId2] ?? null,
        });

        // Build series map for slot labels
        const sMap: Record<string, { team_home: string; team_away: string; round: string }> = {};
        (allSeries as Array<{ id: string; team_home: string; team_away: string; round: string; status: string | null }>).forEach(s => {
          if (s.status !== 'deprecated') {
            sMap[s.id] = { team_home: s.team_home, team_away: s.team_away, round: s.round };
          }
        });
        setSeriesMap(sMap);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'שגיאה בטעינת הנתונים');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId1, userId2]);

  // ── Build comparisons ───────────────────────────────────────────────────────

  const comparisons: SlotComparison[] = [];

  if (user1Data && user2Data) {
    // Collect all unique slot IDs from both users
    const allSlotIds = new Set([
      ...Object.keys(user1Data.picks),
      ...Object.keys(user2Data.picks),
    ]);

    // Separate R1 (DB series IDs) from virtual slots
    const r1Slots: string[] = [];
    const virtualSlotIds = new Set(VIRTUAL_SLOTS.map(v => v.id));

    allSlotIds.forEach(id => {
      if (virtualSlotIds.has(id)) return; // handled separately
      if (seriesMap[id]) {
        r1Slots.push(id);
      } else if (!virtualSlotIds.has(id)) {
        // Unknown slot — might be a virtual or R1 slot we don't have in series
        r1Slots.push(id);
      }
    });

    // Sort R1 by conference (West first, then East)
    r1Slots.sort((a, b) => {
      const sA = seriesMap[a];
      const sB = seriesMap[b];
      if (!sA || !sB) return 0;
      const confA = WEST_TEAMS.has(sA.team_home) ? 0 : 1;
      const confB = WEST_TEAMS.has(sB.team_home) ? 0 : 1;
      return confA - confB;
    });

    // Add R1 comparisons
    r1Slots.forEach(slotId => {
      const series = seriesMap[slotId];
      const label = series ? `${series.team_home} vs ${series.team_away}` : slotId;
      const pick1 = user1Data.picks[slotId] || null;
      const pick2 = user2Data.picks[slotId] || null;

      let match: 'same' | 'different' | 'missing' = 'different';
      if (!pick1 || !pick2) {
        match = 'missing';
      } else if (pick1.winner === pick2.winner) {
        match = 'same';
      }

      comparisons.push({
        slotId,
        label,
        round: series?.round || 'round1',
        pick1,
        pick2,
        match,
      });
    });

    // Add virtual slot comparisons
    VIRTUAL_SLOTS.forEach(vs => {
      const pick1 = user1Data.picks[vs.id] || null;
      const pick2 = user2Data.picks[vs.id] || null;

      // Only add if at least one user has a pick
      if (!pick1 && !pick2) return;

      let match: 'same' | 'different' | 'missing' = 'different';
      if (!pick1 || !pick2) {
        match = 'missing';
      } else if (pick1.winner === pick2.winner) {
        match = 'same';
      }

      comparisons.push({
        slotId: vs.id,
        label: vs.label,
        round: vs.round,
        pick1,
        pick2,
        match,
      });
    });
  }

  // ── Summary stats ──────────────────────────────────────────────────────────

  const totalComparisons = comparisons.filter(c => c.match !== 'missing').length;
  const matchingCount = comparisons.filter(c => c.match === 'same').length;

  // ── Group by round for display ─────────────────────────────────────────────

  const groupedByRound: Record<string, SlotComparison[]> = {};
  comparisons.forEach(c => {
    const roundKey = c.round === 'first_round' ? 'round1' : c.round;
    if (!groupedByRound[roundKey]) groupedByRound[roundKey] = [];
    groupedByRound[roundKey].push(c);
  });

  const orderedRounds = ROUND_ORDER.filter(r => groupedByRound[r]?.length);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-3xl" dir="rtl">
        {/* Back link */}
        <Link
          to="/bracket-leaderboard"
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6"
        >
          <ArrowRight className="w-4 h-4 rotate-180" /> חזרה לדירוג
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-8"
        >
          <GitCompareArrows className="w-10 h-10 text-accent mx-auto mb-3" />
          <h1 className="text-3xl font-bold tracking-tight">השוואת ברקטים</h1>
          {user1Data && user2Data && (
            <div className="flex items-center justify-center gap-3 mt-2 text-sm text-muted-foreground">
              <UserAvatar avatarId={user1Data.avatarId} displayName={user1Data.displayName} size="sm" />
              <span>{user1Data.displayName}</span>
              <span>מול</span>
              <span>{user2Data.displayName}</span>
              <UserAvatar avatarId={user2Data.avatarId} displayName={user2Data.displayName} size="sm" />
            </div>
          )}
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-card border border-destructive/30 rounded-2xl p-6 text-center">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {/* Comparison content */}
        {!loading && !error && user1Data && user2Data && (
          <>
            {/* Summary stats */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="bg-card border border-border rounded-2xl p-4 mb-6 text-center"
            >
              <p className="text-lg font-bold">
                <span className="text-accent tabular-nums">{matchingCount}</span>
                <span className="text-muted-foreground">/{totalComparisons > 0 ? totalComparisons : comparisons.length}</span>
                <span className="text-sm font-normal text-muted-foreground mr-2">ניחושים זהים</span>
              </p>
            </motion.div>

            {/* Column headers (desktop) */}
            <div className="hidden lg:grid grid-cols-[1fr_2fr_2fr] gap-3 mb-3 px-2">
              <span className="section-label text-center">סדרה</span>
              <div className="flex items-center justify-center gap-2">
                <UserAvatar avatarId={user1Data.avatarId} displayName={user1Data.displayName} size="sm" />
                <span className="section-label">{user1Data.displayName}</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <UserAvatar avatarId={user2Data.avatarId} displayName={user2Data.displayName} size="sm" />
                <span className="section-label">{user2Data.displayName}</span>
              </div>
            </div>

            {/* Rounds */}
            <div className="space-y-6">
              {orderedRounds.map((roundKey, ri) => (
                <motion.div
                  key={roundKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + ri * 0.08, duration: 0.35 }}
                >
                  {/* Round header */}
                  <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">
                    {ROUND_LABELS[roundKey] || roundKey}
                  </h2>

                  <div className="space-y-2">
                    {groupedByRound[roundKey].map((comp) => (
                      <ComparisonRow
                        key={comp.slotId}
                        comparison={comp}
                        user1Name={user1Data.displayName}
                        user2Name={user2Data.displayName}
                      />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Empty state */}
            {comparisons.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">אין ניחושים להשוואה</p>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

// ── ComparisonRow ─────────────────────────────────────────────────────────────

interface ComparisonRowProps {
  comparison: SlotComparison;
  user1Name: string;
  user2Name: string;
}

const ComparisonRow = ({ comparison, user1Name, user2Name }: ComparisonRowProps) => {
  const { label, pick1, pick2, match } = comparison;

  const borderClass =
    match === 'same'
      ? 'border-green-500/30'
      : match === 'different'
        ? 'border-accent/40'
        : 'border-border';

  return (
    <div
      className={`bg-card border ${borderClass} rounded-2xl p-4 transition-colors`}
    >
      {/* Series label */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-muted-foreground truncate max-w-[70%]">
          {label}
        </p>
        {match === 'same' && (
          <span className="flex items-center gap-1 text-green-400 text-xs">
            <Check className="w-3.5 h-3.5" /> זהה
          </span>
        )}
        {match === 'different' && (
          <span className="flex items-center gap-1 text-accent text-xs">
            <X className="w-3.5 h-3.5" /> שונה
          </span>
        )}
      </div>

      {/* Picks side by side */}
      <div className="grid grid-cols-2 gap-3">
        <PickCell pick={pick1} userName={user1Name} />
        <PickCell pick={pick2} userName={user2Name} />
      </div>
    </div>
  );
};

// ── PickCell ──────────────────────────────────────────────────────────────────

interface PickCellProps {
  pick: BracketPick | null;
  userName: string;
}

const PickCell = ({ pick, userName }: PickCellProps) => {
  if (!pick) {
    return (
      <div className="bg-background/50 rounded-xl p-3 text-center">
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{userName}</p>
        <p className="text-xs text-muted-foreground/60">לא נבחר</p>
      </div>
    );
  }

  return (
    <div className="bg-background/50 rounded-xl p-3 text-center">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{userName}</p>
      <p className="text-sm font-bold truncate">{pick.winner}</p>
      {pick.predicted_score && (
        <p className="text-xs text-accent mt-0.5 tabular-nums">{pick.predicted_score}</p>
      )}
    </div>
  );
};

export default BracketComparePage;
